"use client";

import { useEffect, useRef, useState } from "react";

interface BFLCarouselProps {
  children: React.ReactNode[] | React.ReactNode;
  slidesPerView?: number;
  autoplay?: boolean;
  interval?: number;
  showNavigation?: boolean;
  showDots?: boolean;
  loop?: boolean;
  className?: string;
  isAddedMargin?: boolean;
}

const BFLCarousel: React.FC<BFLCarouselProps> = ({
  children,
  slidesPerView = 1,
  autoplay = false,
  interval = 3000,
  showNavigation = true,
  showDots = true,
  loop = false,
  className = "",
  isAddedMargin = true,
}) => {
  const [responsiveSlides, setResponsiveSlides] = useState(slidesPerView);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const startX = useRef<number | null>(null);

  const totalSlides = Array.isArray(children) ? children.length : 1;
  const maxIndex = totalSlides - responsiveSlides;
  const slideWidth = 100 / responsiveSlides;

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1199; // Adjust breakpoint as needed
      setIsMobile(mobile);
      setResponsiveSlides(mobile ? 1 : slidesPerView);
      setCurrentIndex(0);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [slidesPerView]);

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const touch = e.touches.item(0);
    if (touch) startX.current = touch.clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (startX.current === null) return;
    const touch = e.changedTouches.item(0);
    const diff = startX.current - touch.clientX;
    if (diff > 50) nextSlide();
    else if (diff < -50) prevSlide();
    startX.current = null;
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => {
      if (prev >= maxIndex) {
        return loop ? 0 : prev;
      }
      return prev + 1;
    });
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => {
      if (prev <= 0) {
        return loop ? maxIndex : 0;
      }
      return prev - 1;
    });
  };

  useEffect(() => {
    if (autoplay) {
      timeoutRef.current = setTimeout(() => nextSlide(), interval);
      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      };
    }
  }, [currentIndex, autoplay, interval]);

  if (Array.isArray(children) && children.length <= 1) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div
      className={`no-scrollbar relative w-full ${isMobile ? "overflow-auto" : "overflow-hidden"} ${className}`}
      onMouseDown={(e) => {
        if (!isMobile) e.preventDefault();
      }}
    >
      <div
        className="flex transition-transform duration-500 ease-in-out"
        style={{
          transform: `translateX(-${currentIndex * (100 / totalSlides)}%)`,
        }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {Array.isArray(children) &&
          children.map((child, index) => (
            <div
              key={"carousel-" + index}
              className={`min-w-0 flex-shrink-0 ${
                isMobile && isAddedMargin && index !== totalSlides - 1
                  ? "mr-[16px]"
                  : ""
              }`}
              style={!isMobile ? { width: `${slideWidth}%` } : undefined}
            >
              {child}
            </div>
          ))}
      </div>

      {showNavigation && !isMobile && (
        <>
          {currentIndex > 0 && (
            <button
              onClick={prevSlide}
              className="text-link-primary absolute left-0 top-[46%] z-10 flex h-10 w-10 -translate-y-1/2 transform items-center justify-center rounded-l-none rounded-r-md bg-white p-3"
              style={{ boxShadow: "rgba(0, 0, 0, 0.2) 0px 0px 10px" }}
            >
              <i className="icomoon text-sm before:content-['\e912']"></i>
            </button>
          )}

          {currentIndex < maxIndex && (
            <button
              onClick={nextSlide}
              className="text-link-primary absolute right-0 top-[46%] z-10 flex h-10 w-10 -translate-y-1/2 transform items-center justify-center rounded-l-md rounded-r-none bg-white p-3"
              style={{ boxShadow: "rgba(0, 0, 0, 0.2) 0px 0px 10px" }}
            >
              <i className="icomoon text-sm before:content-['\e911']"></i>
            </button>
          )}
        </>
      )}

      {showDots && (
        <div className="mt-4 flex justify-center space-x-2">
          {Array.from({ length: maxIndex + 1 }).map((_, index) => (
            <button
              key={"dot-" + index}
              onClick={() => setCurrentIndex(index)}
              className={`h-2.5 w-2.5 rounded-full ${
                currentIndex === index ? "bg-black" : "bg-gray-300"
              }`}
            ></button>
          ))}
        </div>
      )}
    </div>
  );
};

export default BFLCarousel;
